<template>
  <div class="room-selector">
    <label :for="'room' + (index + 1)">Room {{index + 1}}:</label>

    <div class="room-selector__fields">
      <select :id="'room' + (index + 1)" v-model="roomId">
        <option :value="null">Select room type</option>
        <option
          v-for="availableRoom in availableRooms"
          :key="availableRoom.id"
          :value="availableRoom.id"
          :disabled="!roomOptionAvailable(availableRoom)"
        >{{availableRoom.name}}</option>
      </select>

      <button @click="removeRoom" aria-label="Remove room" title="Remove this room">&times;</button>
    </div>

    <template v-if="room.passengers.length">
      <div class="room-selector__room-type">
        <span class="room-selector__people-icon">
          <svg
            width="12"
            height="12"
            role="img"
            aria-hidden="true"
            focusable="false"
            v-for="index in room.occupancy"
            :key="index"
          >
            <use xlink:href="/Static/images/sprites/symbols.svg#icon-room" />
          </svg>
        </span>
        <div class="room-selector__room-description">
          <strong>{{room.name}} Occupancy Room(s)</strong>
          <span>({{room.occupancy}} {{room.occupancy > 1 ? 'people' : 'person'}})</span>
          <small v-if="room.supplement">*{{currency(room.supplement)}} Supplementary Charge</small>
        </div>
      </div>

      <div
        class="room-selector__passenger"
        :class="{ 'room-selector__passengerTs': data.brandNameFormatted === Brand.Travelsphere }"
        v-for="(passenger, passengerIndex) in room.passengers"
        :key="passenger.key"
      >
        <h4
          class="room-selector__passenger-title"
        >{{globalPassengerIndex(index, passengerIndex) | numberToWordsOrdinal | capitalize}} Passenger</h4>

        <div class="room-selector__passenger-row">
          <Input
            name="firstName"
            label="First Name"
            placeholder="First Name"
            :startingValue="autofillName('firstName', index, passengerIndex)"
            :required="true"
            :pattern="patterns.name"
            :title="titles.name"
            :vuexPath="vuexFieldPath(passengerIndex)"
            :trim="true"
            ref="firstName"
            @input.native="saveToSessionStorage('firstName', index, passengerIndex, $event.target.value)"
          />
          <Input
            name="lastName"
            label="Last Name"
            placeholder="Last Name"
            :startingValue="autofillName('lastName', index, passengerIndex)"
            :required="true"
            :pattern="patterns.name"
            :title="titles.name"
            :vuexPath="vuexFieldPath(passengerIndex)"
            :trim="true"
            ref="lastName"
            @input.native="saveToSessionStorage('lastName', index, passengerIndex, $event.target.value)"
          />
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import Input from '@/vue/components/Form/Input.vue';
import ValidationMixin from '@/vue/components/Form/validationMixin';
import {Brand} from './../../../../brand.js';
import debounce from 'lodash/debounce';

export default {
  name: 'Room',
  mixins: [ValidationMixin],

  components: {
    Input
  },

  props: ['data', 'room', 'index'],

  data() {
    return {
      Brand,
      roomId: null
    };
  },

  watch: {
    roomId(roomId) {
      const roomChoice = roomId
        ? this.$store.getters['checkout/availableRoomById'](roomId)
        : { occupancy: 0 };

      const room = {
        ...this.room,
        ...roomChoice
      };

      this.$store.commit('checkout/updateRoom', {
        room,
        index: this.index
      });

      this.populateFields();
    }
  },

  computed: {
    ...mapState('checkout', ['availableRooms', 'loggedInPassengerData']),
    ...mapGetters('checkout', ['globalPassengerIndex', 'currency'])
  },

  methods: {
    removeRoom() {
      this.$store.commit('checkout/removeRoom', this.index);
    },

    roomOptionAvailable(roomOption) {
      const remainingAvailableRooms = this.$store.getters[
        'checkout/remainingAvailableRoomsForRoom'
      ](this.index);

      // check if the room is available in the remaining available rooms
      return remainingAvailableRooms.find(
        remainingAvailableRoom => remainingAvailableRoom.id === roomOption.id
      );
    },

    vuexFieldPath(passengerIndex) {
      return ['checkout', 'rooms', this.index, 'passengers', passengerIndex];
    },

    autofillName(fieldName, index, passengerIndex) {
      if (
        this.globalPassengerIndex(index, passengerIndex) === 1 &&
        this.data.login.userIsLoggedIn &&
        this.loggedInPassengerData
      ) {
        return this.loggedInPassengerData[fieldName];
      }
      return null;
    },

    saveToSessionStorage: debounce(function (fieldName, roomIndex, passengerIndex, value) {
      const storageKey = `room-${roomIndex}-passenger-${passengerIndex}-${fieldName}`;
      sessionStorage.setItem(storageKey, value);
    }, 300),

    populateFields() {
      this.$nextTick(() => {
        this.room.passengers.forEach((passenger, passengerIndex) => {
          if (this.shouldSkipPopulatingPassenger(passengerIndex)) return;

          const storageKeyPrefix = `room-${this.index}-passenger-${passengerIndex}`;
          const { firstName, lastName } = this.getPassengerFields(passenger, storageKeyPrefix);

          this.updatePassengerFields(passenger, firstName, lastName);
        });
      });
    },

    // Skip populating the first room's first passenger if user is logged in
    shouldSkipPopulatingPassenger(passengerIndex) {
      return this.globalPassengerIndex(this.index, passengerIndex) === 1 && this.data.login.userIsLoggedIn && this.loggedInPassengerData;
    },

    getPassengerFields(passenger, storageKeyPrefix) {
      const firstNameKey = `${storageKeyPrefix}-firstName`;
      const lastNameKey = `${storageKeyPrefix}-lastName`;

      return {
        firstName: sessionStorage.getItem(firstNameKey),
        lastName: sessionStorage.getItem(lastNameKey),
      };
    },

    updatePassengerFields(passenger, firstName, lastName) {
      if (passenger.fields) {
        passenger.fields = {
          ...passenger.fields,
          firstName: { ...passenger.fields.firstName, value: firstName, userHasTyped: false, error: false },
          lastName: { ...passenger.fields.lastName, value: lastName, userHasTyped: false, error: false },
        };
      }
    }
  }
};
</script>